// Cave84 — Cellars management (Phase 1 Week 2)
// Spec §4.3 F3.1–F3.3, §6.3 Screen 5.
// Owner-only page (enforced at the route level via <RequireOwner>).
// Shows all cellars with bottle counts and values. Supports Add / Edit / Delete.
// F3.4 (bulk transfer between cellars) is deferred to Phase 1 Week 4.

import { useMemo, useState, useId } from 'react';
import { useForm } from 'react-hook-form';
import { useCellars, useCreateCellar, useUpdateCellar, useDeleteCellar } from '@/hooks/useCellars';
import { useCellarLocations } from '@/hooks/useLocations';
import { useInventory, DEFAULT_FILTERS } from '@/hooks/useInventory';
import { useValuationBasis } from '@/hooks/useValuationBasis';
import { valuateBottle } from '@/utils/valuation';
import type { Cellar, CellarType } from '@cave84/shared-types';
import type { CellarCreateInput, CellarUpdateInput } from '@/services/cellars';
import { Money } from '@/components/Money';

const ALL_FILTERS = { ...DEFAULT_FILTERS, availableAtHome: false };

// ---------------------------------------------------------------------------
// Per-cellar valuation (WI-2/WI-3)
// cellar.total_value_eur is a denormalized field that's never written past
// its 0 initialization (see services/cellars.ts) — compute live instead,
// market price first, cost fallback second, unpriced bottles excluded from
// the sum but counted separately so €0 gaps stay visible.
// ---------------------------------------------------------------------------
interface CellarValuation { value: number; unpriced: number }

function usePerCellarValuation() {
  const { rows } = useInventory(ALL_FILTERS);
  const { basis } = useValuationBasis();

  return useMemo(() => {
    const map = new Map<string, CellarValuation>();
    let totalValue = 0;
    let totalUnpriced = 0;
    for (const r of rows) {
      const e = map.get(r.cellar.id) ?? { value: 0, unpriced: 0 };
      const cost = r.bottle.cost_eur_at_purchase;
      const { value } = basis === 'market'
        ? valuateBottle(r.marketPrice, cost)
        : valuateBottle(null, cost); // cost-only basis: no market fallback
      if (value !== null) {
        e.value += value;
        totalValue += value;
      } else {
        e.unpriced++;
        totalUnpriced++;
      }
      map.set(r.cellar.id, e);
    }
    return { byCellar: map, totalValue, totalUnpriced, basis };
  }, [rows, basis]);
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CELLAR_COLORS = [
  '#C49255', '#7E9A98', '#8C95A8', '#8E663A',
  '#9282A4', '#8FB57C', '#C49255', '#C49255',
];

// ---------------------------------------------------------------------------
// Form types
// ---------------------------------------------------------------------------

interface AddCellarFormValues {
  name: string;
  country: string;
  city: string;
  type: 'physical_abroad' | 'virtual';
  color: string;
  icon: string;
  notes: string;
}

interface EditCellarFormValues {
  name: string;
  country: string;
  city: string;
  color: string;
  icon: string;
  notes: string;
}

// ---------------------------------------------------------------------------
// Shared UI primitives
// ---------------------------------------------------------------------------

const inputClass =
  'w-full bg-surface-2 border border-border-medium rounded px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold transition-colors';
const btnPrimary =
  'px-4 py-2 rounded bg-claret text-text-primary text-xs font-medium hover:bg-claret/80 transition-colors disabled:opacity-50';
const btnSecondary =
  'px-4 py-2 rounded border border-border-medium text-text-secondary text-xs font-medium hover:border-border-strong hover:text-text-primary transition-colors';

function FormField({ label, error, children, className }: {
  label: string; error?: string; children: React.ReactNode; className?: string;
}) {
  return (
    <div className={className}>
      <label className="smallcaps text-text-tertiary block mb-1.5" style={{ fontSize: 11 }}>{label}</label>
      {children}
      {error && <p className="text-claret text-xs mt-1">{error}</p>}
    </div>
  );
}

function ColorSwatch({ color, selected, onClick }: { color: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-7 h-7 rounded-full transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
      style={{ backgroundColor: color, outline: selected ? `2px solid ${color}` : 'none', outlineOffset: selected ? '2px' : '0', opacity: selected ? 1 : 0.6 }}
      aria-label={`Color ${color}`}
      aria-pressed={selected}
    />
  );
}

function ModalOverlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className="relative z-10 w-full max-w-md">{children}</div>
    </div>
  );
}

function ModalPanel({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="rounded-xl border border-border-medium bg-surface-1 shadow-2xl">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle">
        <h2 className="font-display text-base font-medium text-text-primary">{title}</h2>
        <button type="button" onClick={onClose} className="text-text-tertiary hover:text-text-primary transition-colors" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" className="w-5 h-5"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Domain helpers
// ---------------------------------------------------------------------------

function CellarTypeBadge({ type }: { type: CellarType }) {
  const labels: Record<CellarType, string> = { physical_home: 'Home', physical_abroad: 'Abroad', virtual: 'Virtual' };
  return <span className="smallcaps text-text-muted">{labels[type]}</span>;
}

function CountryFlag({ country }: { country: string }) {
  const upper = (country ?? '').toUpperCase();
  if (upper.length !== 2) return null;
  const flag = [...upper].map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65)).join('');
  return <span aria-label={upper}>{flag}</span>;
}

// ---------------------------------------------------------------------------
// Home Cellar fridge breakdown
// ---------------------------------------------------------------------------

function HomeCellarLocations({ cellarId }: { cellarId: string }) {
  const { data: locations, isLoading } = useCellarLocations(cellarId);
  if (isLoading) return <p className="text-text-muted text-xs mt-2 animate-pulse">Loading...</p>;
  if (!locations?.length) return <p className="text-text-muted text-xs mt-2">No locations recorded.</p>;
  return (
    <div className="mt-3 pt-3 border-t border-border-subtle grid grid-cols-2 gap-x-4 gap-y-1">
      {locations.map((loc) => (
        <div key={loc.id} className="flex items-center justify-between gap-2">
          <span className="text-text-secondary text-xs truncate">{loc.name}</span>
          <span className="mono text-text-tertiary text-xs tabular-nums">{loc.current_count}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cellar Card
// ---------------------------------------------------------------------------

function CellarCard({ cellar, valuation, basis, onEdit, onDelete }: {
  cellar: Cellar; valuation: CellarValuation; basis: 'cost' | 'market';
  onEdit: (c: Cellar) => void; onDelete: (c: Cellar) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const canDelete = cellar.bottle_count === 0 && !cellar.is_home;

  return (
    <div className="rounded-lg border border-border-subtle bg-surface-1 overflow-hidden" style={{ borderLeftColor: cellar.color, borderLeftWidth: 3 }}>
      <div className="p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-xl" style={{ backgroundColor: `${cellar.color}22` }}>
          {cellar.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-display text-base font-medium text-text-primary truncate">{cellar.name}</span>
            <CellarTypeBadge type={cellar.type} />
          </div>
          <div className="flex items-center gap-1.5 text-text-tertiary text-xs">
            {cellar.country && <><CountryFlag country={cellar.country} /><span>{cellar.city ?? cellar.country}</span></>}
          </div>
          {cellar.notes && <p className="text-text-muted text-xs mt-1 line-clamp-1">{cellar.notes}</p>}
        </div>
        <div className="flex-shrink-0 text-right">
          <p className="mono text-text-primary text-lg tabular-nums leading-tight">{cellar.bottle_count.toLocaleString()}</p>
          <p className="smallcaps text-text-muted" style={{ fontSize: 11 }}>bottles</p>
          {(valuation.value > 0 || valuation.unpriced > 0) && (
            <p className="mono text-text-tertiary text-xs tabular-nums mt-0.5">
              <Money value={valuation.value} currency="EUR" minWidthCh={7} />
              <span className="text-text-muted" style={{ fontSize: 11 }}> {basis}</span>
              {valuation.unpriced > 0 && (
                <span className="text-text-muted italic" style={{ fontSize: 11 }}> · {valuation.unpriced} unpriced</span>
              )}
            </p>
          )}
        </div>
      </div>

      {cellar.is_home && (
        <div className="px-4 pb-3">
          <button type="button" onClick={() => setExpanded((v) => !v)}
            className="smallcaps text-text-tertiary hover:text-text-secondary transition-colors flex items-center gap-1" style={{ fontSize: 11 }}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`}>
              <path d="M3 6l5 5 5-5" />
            </svg>
            {expanded ? 'Hide locations' : 'Show locations'}
          </button>
          {expanded && <HomeCellarLocations cellarId={cellar.id} />}
        </div>
      )}

      <div className="px-4 py-2 border-t border-border-subtle flex items-center justify-end gap-3">
        <button type="button" onClick={() => onEdit(cellar)} className="smallcaps text-text-tertiary hover:text-gold transition-colors" style={{ fontSize: 11 }}>
          Edit
        </button>
        {!cellar.is_home && (
          <button
            type="button"
            onClick={() => { if (canDelete) onDelete(cellar); }}
            disabled={!canDelete}
            title={canDelete ? undefined : 'Move all bottles out first'}
            className={`smallcaps transition-colors ${canDelete ? 'text-text-tertiary hover:text-claret cursor-pointer' : 'text-text-muted cursor-not-allowed'}`}
            style={{ fontSize: 11 }}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add Cellar Modal
// ---------------------------------------------------------------------------

function AddCellarModal({ onClose }: { onClose: () => void }) {
  const createCellar = useCreateCellar();
  const formId = useId();
  const { register, handleSubmit, setValue, watch, formState: { errors, isSubmitting } } =
    useForm<AddCellarFormValues>({
      defaultValues: { type: 'virtual', color: CELLAR_COLORS[4], icon: '📦', country: '', city: '', name: '', notes: '' },
    });
  const selectedColor = watch('color');
  const selectedType = watch('type');

  const onSubmit = async (values: AddCellarFormValues) => {
    const isVirtual = values.type === 'virtual';
    const input: CellarCreateInput = {
      name: values.name,
      country: isVirtual ? '' : values.country.toUpperCase(),
      city: isVirtual ? undefined : (values.city || undefined),
      type: values.type,
      is_home: false,
      is_virtual: isVirtual,
      color: values.color,
      icon: values.icon,
      sort_order: 99,
      notes: values.notes || undefined,
    };
    await createCellar.mutateAsync(input);
    onClose();
  };

  return (
    <ModalOverlay onClose={onClose}>
      <ModalPanel title="Add Cellar" onClose={onClose}>
        <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <span className="smallcaps text-text-tertiary block mb-1.5" style={{ fontSize: 11 }}>Type</span>
            <div className="flex gap-2">
              {(['virtual', 'physical_abroad'] as const).map((t) => (
                <button key={t} type="button" onClick={() => setValue('type', t)}
                  className={`flex-1 py-2 rounded border text-xs font-medium transition-all ${selectedType === t ? 'border-gold text-gold bg-surface-3' : 'border-border-medium text-text-secondary bg-surface-2 hover:border-border-strong'}`}>
                  {t === 'virtual' ? 'Virtual' : 'Physical Abroad'}
                </button>
              ))}
            </div>
          </div>

          <FormField label="Name" error={errors.name?.message}>
            <input {...register('name', { required: 'Name is required', maxLength: { value: 64, message: 'Max 64 chars' } })} className={inputClass} placeholder="London" />
          </FormField>

          {selectedType === 'physical_abroad' && (
            <div className="flex gap-3">
              <FormField label="Country (ISO-2)" error={errors.country?.message} className="w-28">
                <input {...register('country', { required: 'Required', minLength: { value: 2, message: 'ISO-2 code' }, maxLength: { value: 2, message: 'ISO-2 only' } })} className={inputClass} placeholder="GB" maxLength={2} />
              </FormField>
              <FormField label="City" error={errors.city?.message} className="flex-1">
                <input {...register('city', { maxLength: { value: 64, message: 'Max 64' } })} className={inputClass} placeholder="London" />
              </FormField>
            </div>
          )}

          <FormField label="Icon (emoji)" error={errors.icon?.message}>
            <input {...register('icon', { required: 'Required', maxLength: { value: 8, message: 'Too long' } })} className={`${inputClass} w-24`} placeholder="🍾" maxLength={8} />
          </FormField>

          <div>
            <span className="smallcaps text-text-tertiary block mb-1.5" style={{ fontSize: 11 }}>Color</span>
            <div className="flex flex-wrap gap-2">
              {CELLAR_COLORS.map((c) => <ColorSwatch key={c} color={c} selected={selectedColor === c} onClick={() => setValue('color', c)} />)}
            </div>
            {errors.color && <p className="text-claret text-xs mt-1">{errors.color.message}</p>}
          </div>

          <FormField label="Notes (optional)" error={errors.notes?.message}>
            <textarea {...register('notes', { maxLength: { value: 512, message: 'Max 512' } })} className={`${inputClass} resize-none`} rows={2} placeholder="Storage conditions..." />
          </FormField>

          {createCellar.error && <p className="text-claret text-xs">{createCellar.error.message}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className={btnSecondary}>Cancel</button>
            <button type="submit" disabled={isSubmitting || createCellar.isPending} className={btnPrimary}>
              {isSubmitting ? 'Creating...' : 'Create Cellar'}
            </button>
          </div>
        </form>
      </ModalPanel>
    </ModalOverlay>
  );
}

// ---------------------------------------------------------------------------
// Edit Cellar Modal
// ---------------------------------------------------------------------------

function EditCellarModal({ cellar, onClose }: { cellar: Cellar; onClose: () => void }) {
  const updateCellar = useUpdateCellar();
  const formId = useId();
  const { register, handleSubmit, setValue, watch, formState: { errors, isSubmitting } } =
    useForm<EditCellarFormValues>({
      defaultValues: { name: cellar.name, country: cellar.country ?? '', city: cellar.city ?? '', color: cellar.color, icon: cellar.icon, notes: cellar.notes ?? '' },
    });
  const selectedColor = watch('color');

  const onSubmit = async (values: EditCellarFormValues) => {
    const patch: CellarUpdateInput = {
      name: values.name,
      country: values.country || undefined,
      city: values.city || undefined,
      color: values.color,
      icon: values.icon,
      notes: values.notes || undefined,
    };
    await updateCellar.mutateAsync({ id: cellar.id, patch });
    onClose();
  };

  return (
    <ModalOverlay onClose={onClose}>
      <ModalPanel title={`Edit — ${cellar.name}`} onClose={onClose}>
        <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="flex items-center gap-2 pb-1">
            <span className="text-xl">{cellar.icon}</span>
            <CellarTypeBadge type={cellar.type} />
          </div>

          <FormField label="Name" error={errors.name?.message}>
            <input {...register('name', { required: 'Name is required', maxLength: { value: 64, message: 'Max 64 chars' } })} className={inputClass} />
          </FormField>

          {!cellar.is_virtual && (
            <div className="flex gap-3">
              <FormField label="Country (ISO-2)" error={errors.country?.message} className="w-28">
                <input {...register('country', { maxLength: { value: 2, message: 'ISO-2 only' } })} className={inputClass} maxLength={2} />
              </FormField>
              <FormField label="City" error={errors.city?.message} className="flex-1">
                <input {...register('city', { maxLength: { value: 64, message: 'Max 64' } })} className={inputClass} />
              </FormField>
            </div>
          )}

          <FormField label="Icon (emoji)" error={errors.icon?.message}>
            <input {...register('icon', { required: 'Required', maxLength: { value: 8, message: 'Too long' } })} className={`${inputClass} w-24`} maxLength={8} />
          </FormField>

          <div>
            <span className="smallcaps text-text-tertiary block mb-1.5" style={{ fontSize: 11 }}>Color</span>
            <div className="flex flex-wrap gap-2">
              {CELLAR_COLORS.map((c) => <ColorSwatch key={c} color={c} selected={selectedColor === c} onClick={() => setValue('color', c)} />)}
            </div>
          </div>

          <FormField label="Notes (optional)" error={errors.notes?.message}>
            <textarea {...register('notes', { maxLength: { value: 512, message: 'Max 512' } })} className={`${inputClass} resize-none`} rows={2} />
          </FormField>

          {updateCellar.error && <p className="text-claret text-xs">{updateCellar.error.message}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className={btnSecondary}>Cancel</button>
            <button type="submit" disabled={isSubmitting || updateCellar.isPending} className={btnPrimary}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </ModalPanel>
    </ModalOverlay>
  );
}

// ---------------------------------------------------------------------------
// Delete Confirm Modal
// ---------------------------------------------------------------------------

function DeleteCellarModal({ cellar, onClose }: { cellar: Cellar; onClose: () => void }) {
  const deleteCellar = useDeleteCellar();
  return (
    <ModalOverlay onClose={onClose}>
      <ModalPanel title="Delete Cellar" onClose={onClose}>
        <p className="text-text-secondary text-sm mb-4">
          Delete <span className="font-display text-text-primary">{cellar.name}</span>? This cannot be undone.
        </p>
        {deleteCellar.error && <p className="text-claret text-xs mb-3">{deleteCellar.error.message}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={btnSecondary}>Cancel</button>
          <button
            type="button"
            onClick={async () => { await deleteCellar.mutateAsync(cellar.id); onClose(); }}
            disabled={deleteCellar.isPending}
            className="px-4 py-2 rounded border border-claret bg-claret/10 text-claret text-xs font-medium hover:bg-claret/20 transition-colors disabled:opacity-50"
          >
            {deleteCellar.isPending ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </ModalPanel>
    </ModalOverlay>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type ModalState =
  | { kind: 'none' }
  | { kind: 'add' }
  | { kind: 'edit'; cellar: Cellar }
  | { kind: 'delete'; cellar: Cellar };

export default function CellarsPage() {
  const { data: cellars, isLoading, error } = useCellars();
  const [modal, setModal] = useState<ModalState>({ kind: 'none' });
  const { byCellar, totalValue, totalUnpriced, basis } = usePerCellarValuation();
  const { setBasis } = useValuationBasis();

  const totalBottles = cellars?.reduce((s, c) => s + c.bottle_count, 0) ?? 0;
  const emptyValuation: CellarValuation = { value: 0, unpriced: 0 };

  return (
    <div className="relative z-10 max-w-3xl mx-auto px-4 py-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="smallcaps text-text-tertiary mb-1">Locations</p>
          <h1 className="font-display text-3xl font-light text-text-primary tracking-tight">Cellars</h1>
        </div>
        <button type="button" onClick={() => setModal({ kind: 'add' })} className={btnPrimary}>
          + Add Cellar
        </button>
      </div>

      {!isLoading && totalBottles > 0 && (
        <div className="flex items-center gap-6 mb-6 px-4 py-3 rounded-lg border border-border-subtle bg-surface-1">
          <div>
            <p className="mono text-text-primary text-xl tabular-nums">{totalBottles.toLocaleString()}</p>
            <p className="smallcaps text-text-muted" style={{ fontSize: 11 }}>Total bottles</p>
          </div>
          <div className="w-px bg-border-subtle self-stretch" />
          <div>
            <p className="mono text-text-primary text-xl tabular-nums"><Money value={totalValue} currency="EUR" minWidthCh={7} /></p>
            <p className="smallcaps text-text-muted" style={{ fontSize: 11 }}>
              {basis === 'market' ? 'Market value' : 'Cost basis'}
              {totalUnpriced > 0 && <span className="italic"> &middot; {totalUnpriced} unpriced</span>}
            </p>
          </div>
          <div className="w-px bg-border-subtle self-stretch" />
          <div>
            <p className="mono text-text-primary text-xl tabular-nums">{cellars?.length ?? 0}</p>
            <p className="smallcaps text-text-muted" style={{ fontSize: 11 }}>Cellars</p>
          </div>
          <div className="ml-auto flex rounded-full border border-border-medium overflow-hidden flex-shrink-0">
            {(['market', 'cost'] as const).map((b) => (
              <button
                key={b}
                type="button"
                onClick={() => setBasis(b)}
                className="smallcaps px-2.5 py-1 transition-colors"
                style={{
                  fontSize: 11,
                  background: basis === b ? 'rgba(196,146,85,0.16)' : 'transparent',
                  color: basis === b ? '#C49255' : undefined,
                }}
              >
                {b === 'market' ? 'Market' : 'Cost'}
              </button>
            ))}
          </div>
        </div>
      )}

      {isLoading && <p className="text-text-tertiary text-sm animate-pulse">Loading cellars...</p>}
      {error && <p className="text-claret text-sm">Failed to load: {error.message}</p>}

      {cellars && cellars.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[...cellars].sort((a, b) => a.sort_order - b.sort_order).map((cellar) => (
            <CellarCard
              key={cellar.id}
              cellar={cellar}
              valuation={byCellar.get(cellar.id) ?? emptyValuation}
              basis={basis}
              onEdit={(c) => setModal({ kind: 'edit', cellar: c })}
              onDelete={(c) => setModal({ kind: 'delete', cellar: c })}
            />
          ))}
        </div>
      )}

      {!isLoading && cellars?.length === 0 && (
        <div className="text-center py-16">
          <p className="font-display text-2xl text-text-tertiary mb-2">No cellars yet</p>
          <p className="text-text-muted text-sm">Add your first cellar above.</p>
        </div>
      )}

      {modal.kind === 'add' && <AddCellarModal onClose={() => setModal({ kind: 'none' })} />}
      {modal.kind === 'edit' && <EditCellarModal cellar={modal.cellar} onClose={() => setModal({ kind: 'none' })} />}
      {modal.kind === 'delete' && <DeleteCellarModal cellar={modal.cellar} onClose={() => setModal({ kind: 'none' })} />}
    </div>
  );
}
